import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppOfflineStore } from '../app.offlineService';
import { BaseSvcService } from './config/base-svc.service';
import {
  HandleError,
  HttpErrorHandlerService,
} from './config/http-error-handler.service';
import { SessionService } from './session.service';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  IGetQuestionRelationshipResponse,
  IGetRelatedQuestionsPayload,
  IQuestionsWithAnswersResponse,
  IRelatedQuestionRequestPayload,
} from '../interfaces/questionManagement';
import { IPracticeQuestions } from '../Models/QuestionMaster';
export interface IGetQuestionPayload {
  LoggedInUser: string;
  SessionLanguageTypeCode: string;
  SubjectId?: number | null;
  QuestionId: string | null;
  ScreenName: string;
}
export interface ICreateUpdateApplicableQuestionPayload {
  LoggedInUser: string;
  ErrorMessage: string;
  QuestionApplicableCourseCategoryList: QuestionApplicableCourseCategoryList[];
  QuestionApplicableCourseList: QuestionApplicableCourseList[];
}

export interface QuestionApplicableCourseCategoryList {
  QuestionID: string;
  CourseCategoryTypeCode: string;
  SelectedIND: string;
}

export interface QuestionApplicableCourseList {
  QuestionID: string;
  CourseID: number;
  PreviouslyAskedIND: string;
  SelectedIND: string;
  QuestionDifficultyLevelTypeCode:string;
}
export interface IGetQuestionApplicablePayload {
  LoggedInUser: string;
  SubjectId: number | null;
  QuestionId: string | null;
  SelectedInd: string;
  ScreenName: string;
}
export interface IApplicableQuestionCoursePayload {
  LoggedInUser: string;
  SessionLanguageTypeCode: string;
  ScreenName: string;
  QuestionID: string | null;
  CourseID: any;
  SubjectId: number | null;
  SelectedInd: string;
}

export interface IGetPathPayload {
  LoggedInUserID: string;
  SubjectId: number | null;
  Screenname: string;
}

export interface IRelocateQuestionPayload {
  LoggedInUser?: string;
  UDTRelocateMultipleQuestions: {
    QuestionID: string;
  }[];
  SubjectID: number;
  ErrorMessage?: string;
  SuccessIND?: string; // 'Y' or 'N'
}

export interface IProcessedPracticeQuestion {
  Question_ID: string;
  Language_Type_Code: string;
  Question_Status_Type_Code: string;
  Question_Status_Type_Name: string;
  Multi_Part_Question_Ind: string;
  Question_Text: string;
  Created_By: string;
  Creation_Date: string;
  isChecked: boolean;
  Subject_Path_Display_order: any;
  Question_Display_Order: any;
  Selected_IND: string | null;
  selectedForRelocation: boolean;
  AllAnswers: {
    Answer_Choice_ID: number;
    Right_Answer_Ind: any;
    Answer_Choice_Text: string;
  }[];
}

export interface IPracticeQuestionSelectionPayload {
  LoggedInUser?: string;
  StudyMaterialID: number;
  UDTStudyMaterialPracticeQuestionSelected: string[];
}

export interface ISubjectList {
  Subject_Id: number
  Parent_Subject_Id: number
  Subject_Name: string
  Subject_Level_Type_Code: string
  Display_Order: number
  Child_Topic_Exists_Ind: string;
  children?: ISubjectList[];
}

export interface PreEvaluationQuestion {
  Question_ID: string;
  Selected_Ind: 'Y' | 'N';
}
@Injectable({
  providedIn: 'root',
})
export class QuestionManagementService {
  private handleError: HandleError;
  public url: string;
  public options;

  constructor(
    private http: HttpClient,
    public session: SessionService,
    private appserv: AppOfflineStore,
    public httpErrorHandler: HttpErrorHandlerService,
    private config: BaseSvcService
  ) {
    this.handleError = httpErrorHandler.createHandleError('HeroesService');
    this.url = this.config.activeURL;
    this.options = this.config._httpoption;
  }

getQuestion$(data: Partial<IGetQuestionPayload>): Observable<any[]> {
  // keep existing context
  data.LoggedInUser = this.session.getUser();
  data.SessionLanguageTypeCode = this.appserv.userlanuage;

  // try to use SubjectId if provided
  const subjectId = (data as any)?.SubjectId;

  return this.http
    .post(`${this.url}api/GetQuestion`, data, this.options)
    .pipe(
      map((response: any) => this.processData(response?.resultList)),
      switchMap((processedQuestions: any[]) => {
        // If no subjectId, return the processed questions as-is
        if (subjectId == null) return of(processedQuestions);

        // Otherwise, fetch previous-questions list and enrich
        return forkJoin({
          questions: of(processedQuestions),
          prevList: this.getPreviousQuestions(subjectId).pipe(
            map((res: any) => (Array.isArray(res) ? res : res?.resultList ?? [])),
            catchError(() => of([])) // fail-safe
          ),
        }).pipe(
          map(({ questions, prevList }) => {
            // Build Code -> unique Descriptions[] map
            const codeToDescs = new Map<string, Set<string>>();

            for (const item of prevList) {
              const code = item?.Code as string | undefined;
              const desc = item?.Description as string | undefined;
              if (!code || !desc) continue;
              if (!codeToDescs.has(code)) codeToDescs.set(code, new Set<string>());
              codeToDescs.get(code)!.add(desc);
            }

            // How to derive the "code" from a question (adjust if needed)
            const getQuestionCode = (q: any): string | undefined => q?.Question_ID;

            return questions.map((q: any) => {
              const code = getQuestionCode(q);
              const allDescs =
                code && codeToDescs.has(code) ? Array.from(codeToDescs.get(code)!) : [];

              // Optional: sort if you want stable order
              // allDescs.sort();

              const previouslyAppearedDescription = allDescs[0] ?? null;

              return {
                ...q,
                previouslyAppearedDescription,       // first match
                previouslyAppearedDescriptions: allDescs, // all matches
              };
            });
          })
        );
      })
    );
}

getQuestionAndAnswers$(
  data: Partial<IGetQuestionPayload>
): Observable<any[]> {
  data.LoggedInUser = this.session.getUser();
  data.SessionLanguageTypeCode = this.appserv.userlanuage;

  const subjectId = (data as any)?.SubjectId;

  return this.http
    .post(`${this.url}api/GetQuestionAndAnswer`, data, this.options)
    .pipe(
      map((response: any) => this.processData(response.resultList)),
      switchMap((processedQuestions: any[]) => {
        if (subjectId == null) return of(processedQuestions);

        return forkJoin({
          questions: of(processedQuestions),
          prevList: this.getPreviousQuestions(subjectId).pipe(
            map((res: any) => (Array.isArray(res) ? res : res?.resultList ?? [])),
            catchError(() => of([]))
          ),
        }).pipe(
          map(({ questions, prevList }) => {
            // Build Code -> unique Descriptions[] map (use Set for dedupe)
            const codeToDescs = new Map<string, Set<string>>();
            // If you need ordering by Display_Order or recency, collect objects then sort.
            for (const item of prevList) {
              const code = item?.Code as string | undefined;
              const desc = item?.Description as string | undefined;
              if (!code || !desc) continue;
              if (!codeToDescs.has(code)) codeToDescs.set(code, new Set<string>());
              codeToDescs.get(code)!.add(desc);
            }

            const getQuestionCode = (q: any): string | undefined =>
              q?.Question_ID ;

            return questions.map((q: any) => {
              const code = getQuestionCode(q);
              const allDescs = code && codeToDescs.has(code)
                ? Array.from(codeToDescs.get(code)!)
                : [];

              // Optional: stable sort (alphabetical). Remove if not needed.
              // allDescs.sort();

              const previouslyAppearedDescription = allDescs[0] ?? null;

              return {
                ...q,
                previouslyAppearedDescription,      // first match
                previouslyAppearedDescriptions: allDescs, // all matches
              };
            });
          })
        );
      })
    );
}
  getPreviousQuestions(subjectId:number){
    return  this.http.get(`${this.url}api/GetCodes/QUESTION_PREVIOUSLY_ASKED_EXAM@${subjectId}`);
  }


  createUpdateApplicableQuestion$(
    data: Partial<ICreateUpdateApplicableQuestionPayload>
  ): Observable<any> {
    data.LoggedInUser = this.session.getUser();
    return this.http.post(
      `${this.url}api/InsertOrUpdateQuestionApplicableCourse`,
      data,
      this.options
    );
  }

  getQuestionApplicableCourseCategory$(
    data: Partial<IGetQuestionApplicablePayload>
  ): Observable<any> {
    data.LoggedInUser = this.session.getUser();
    return this.http
      .post(
        `${this.url}api/GetQuestionApplicableCourseCategory`,
        data,
        this.options
      )
      .pipe(
        map((e: any) => {
          return e.result;
        })
      );
  }
  getQuestionApplicableCourse$(
    data: Partial<IApplicableQuestionCoursePayload>
  ): Observable<any> {
    data.LoggedInUser = this.session.getUser();
    return this.http
      .post(`${this.url}api/GetQuestionApplicableCourse`, data, this.options)
      .pipe(
        map((e: any) => {
          return e.result;
        })
      );
  }
  getPath$(data: Partial<IGetPathPayload>): Observable<string> {
    data.LoggedInUserID = this.session.getUser();
    return this.http.post(`${this.url}api/GetPath`, data, this.options).pipe(
      map((res: any) => {
        return res.Data;
      })
    );
  }

  processData(data: IQuestionsWithAnswersResponse[]) {
    const questionMap: { [key: string]: any } = {};

    data.forEach((item) => {
      const questionId = item.Question_ID;

      if (!questionMap[questionId]) {
        questionMap[questionId] = {
          Question_ID: item.Question_ID,
          Question_Template_ID: item.Question_Template_ID,
          Language_Type_Code: item.Language_Type_Code,
          Recommended_Response_Time_Type_Code:
            item.Recommended_Response_Time_Type_Code,
          Recommended_Response_Time_Type_Name:
            item.Recommended_Response_Time_Type_Name,
          Question_Gk_Type_Code: item.Question_Gk_Type_Code,
          Question_Gk_Type_Name: item.Question_Gk_Type_Name,
          Question_Status_Type_Code: item.Question_Status_Type_Code,
          Question_Status_Type_Name: item.Question_Status_Type_Name,
          Location_ID: item.Location_ID,
          Location_Name: item.Location_Name,
          Multi_Part_Question_Ind: item.Multi_Part_Question_Ind,
          Allow_Translation_Ind: item.Allow_Translation_Ind,
          Comment_Text: item.Comment_Text,
          Question_Text: item.Question_Text,
          Question_Search_Text: item.Question_Search_Text,
          Created_By: item.Created_By,
          Creation_Date: item.Creation_Date,
          Updated_Date:item.Updation_Date,
          Updated_By: item.Updated_By,
          Display_Order: item.Display_Order,
          selectedForRelocation:false,
          AllAnswers: [],
        };
      }

      // Add the answer to the AllAnswers array
      questionMap[questionId].AllAnswers.push({
        Answer_Type_Code: item.Answer_Type_Code,
        Answer_Type_Name: item.Answer_Type_Name,
        Answer_Choice_ID: item.Answer_Choice_ID,
        Right_Answer_Ind: item.Right_Answer_Ind,
        Answer_Choice_Text: item.Answer_Choice_Text,
      });
    });
    // sorting the right answers to first
    Object.values(questionMap).forEach((question: any) => {
      question.AllAnswers.sort((a: any, b: any) => {
        return a.Right_Answer_Ind === 'Y' && b.Right_Answer_Ind !== 'Y'
          ? -1
          : 1;
      });
    });

    // Return an array of the grouped questions
    return Object.values(questionMap);
  }

  getQuestionRealtionType$(data: { Tablename: string }): Observable<any[]> {
    const url = `${this.url}api/GetCodes/${data.Tablename}`;
    return this.http.get<{ resultList: any[] }>(url, this.options).pipe(
      map((response) => response.resultList),
      catchError(this.handleError<any[]>('getQuestion', []))
    );
  }

  getRelatedQuestions$(
    data: Partial<IGetRelatedQuestionsPayload>
  ): Observable<IGetQuestionRelationshipResponse[]> {
    data.LoggedInUser = this.session.getUser();
    return this.http
      .post(`${this.url}api/GetRelatedQuestion`, data, this.options)
      .pipe(
        map((res: any) => {
          return res.resultList;
        })
      );
  }
  InsertORUpdateRelatedQuestions$(
    data: Partial<IRelatedQuestionRequestPayload>
  ) {
    data.LoggedInUser = this.session.getUser();
    return this.http
      .post(`${this.url}api/InsertOrUpdateRelatedQuestion`, data, this.options)
      .pipe(
        map((res: any) => {
          return res.Data;
        })
      );
  }

  updateQuestionDisplayOrder$(
    questions: { QuestionID: string; DisplayOrder: number }[]
  ): Observable<any> {
    const payload = {
      LoggedInUser: this.session.getUser(),
      UDTQuestion: questions,
    };

    return this.http.post(
      `${this.url}api/UpdateQuestionDisplayOrder`,
      payload,
      this.options
    );
  }
  relocateQuestions$(data: Partial<IRelocateQuestionPayload>): Observable<any> {
    return this.http.post(`${this.url}api/RelocateMultipleQuestions`, data, this.options);
  }

  getPraticeQuestion(params: any): Observable<any> {
    params.LoggedInUser = this.session.getUser();
    params.SessionLanguageTypeCode = this.appserv.userlanuage;
    params.ScreenName = "ASSIGN_PRACTICE_QUESTION"
    // const httpParams = new HttpParams({ fromObject: params });

    return this.http.post<{ parcticeQuestionResults: any[] }>(
      `${this.url}api/GetQuestionForPractice`,
     params
    ).pipe(map((res)=> this.processPracticeQuestions(res.parcticeQuestionResults)))
  }

  processPracticeQuestions(data: IPracticeQuestions[]) {
    const questionMap: { [key: string]: any } = {};

    data.forEach((item) => {
      const questionId = item.Question_ID;

      if (!questionMap[questionId]) {
        questionMap[questionId] = {
          Question_ID: item.Question_ID,
          Language_Type_Code: item.Language_Type_Code,
          Question_Status_Type_Code: item.Question_Status_Type_Code,
          Question_Status_Type_Name: item.Question_Status_Type_Name,
          Multi_Part_Question_Ind: item.Multi_Part_Question_Ind,
          Question_Text: item.Question_Text,
          Created_By: item.Created_By,
          Creation_Date: item.Creation_Date,
          Subject_Path_Display_order: item.Subject_Path_Display_order,
          Question_Display_Order: item.Question_Display_Order,
          Selected_IND: item.Selected_IND,
          selectedForRelocation: false,
          isChecked:item.Selected_IND === 'Y',
          AllAnswers: [],
        };
      }

      // Add the answer to the AllAnswers array
      questionMap[questionId].AllAnswers.push({
        Answer_Choice_ID: item.Answer_Choice_ID,
        Right_Answer_Ind: item.Right_Answer_Ind,
        Answer_Choice_Text: item.Answer_Choice_Text,
      });
    });

    // Sort answers so right ones come first
    Object.values(questionMap).forEach((question: any) => {
      question.AllAnswers.sort((a: any, b: any) => {
        return a.Right_Answer_Ind === 'Y' && b.Right_Answer_Ind !== 'Y'
          ? -1
          : 1;
      });
    });

    return Object.values(questionMap);
  }

  InsertOrUpdateStudyMaterialPracticeQuestion$(params:IPracticeQuestionSelectionPayload){
    params.LoggedInUser = this.session.getUser();
    return this.http.post<{ parcticeQuestionResults: any[] }>(
      `${this.url}api/InsertOrUpdateStudyMaterialPracticeQuestion`,
     params
    )
  }

  getSubjectList(params: any): Observable<ISubjectList[]> {
    params.LoggedInUser = this.session.getUser();
    params.ScreenName = "";
    return this.http
      .post<{ SubjectList: ISubjectList[] }>(`${this.url}api/GetSubjectList`, params)
      .pipe(map(res => res.SubjectList));
  }


getPreEvaluationQuestions(
  CourseID: number,
  SubjectID: number
): Observable<PreEvaluationQuestion[]> {

  const body = {
    LoggedInUser: this.session.getUser(),
    CourseID,
    SubjectID
  };

  return this.http.post<{ data: PreEvaluationQuestion[] }>(
    `${this.url}api/GetCoursePreEvaluationQuestion`,
    body
  ).pipe(
    map(res => {

      return res.data ?? [];
    })
  );
}

saveCoursePreEvaluationQuestions(payload: {
  LoggedInUser?: string;
  CourseID: number;
  SubjectID: number;
  UDTCoursePreEvaluationQuestion: {
    QuestionID: string;
    SelectedIND: 'Y' | 'N';
  }[];
}) {
  payload.LoggedInUser = this.session.getUser();
  return this.http.post(
    `${this.url}api/InsertOrUpdateCoursePreEvaluationQuestion`,
    payload
  );
}






}
